export const DEFAULT_GAME_RULES = Object.freeze({
  keepInventoryOnDeath: false,
  creativeMode: false,
  showCoordinates: true
});

export const GAME_RULES_STORAGE_KEY = 'minecraft_game_rules_v1';

function toBool(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return !!fallback;
}

export function createGameRulesStore({
  storage = null,
  storageKey = GAME_RULES_STORAGE_KEY,
  defaults = DEFAULT_GAME_RULES
} = {}) {
  const resolvedStorage = storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
  let rules = {
    keepInventoryOnDeath: !!defaults.keepInventoryOnDeath,
    creativeMode: !!defaults.creativeMode,
    showCoordinates: toBool(defaults.showCoordinates, true)
  };

  function getRules() {
    return { ...rules };
  }

  function read() {
    if (!resolvedStorage) return getRules();
    try {
      const raw = resolvedStorage.getItem(storageKey);
      if (!raw) return getRules();
      const parsed = JSON.parse(raw);
      rules = {
        keepInventoryOnDeath: toBool(parsed?.keepInventoryOnDeath, defaults.keepInventoryOnDeath),
        creativeMode: toBool(parsed?.creativeMode, defaults.creativeMode),
        showCoordinates: toBool(parsed?.showCoordinates, defaults.showCoordinates)
      };
    } catch {
      // Ignore invalid storage payload and keep in-memory defaults.
    }
    return getRules();
  }

  function write() {
    if (!resolvedStorage) return;
    try {
      resolvedStorage.setItem(storageKey, JSON.stringify(rules));
    } catch {
      // Ignore storage failures (private mode / quota / permissions).
    }
  }

  function setRules(partial = {}, { persist = true } = {}) {
    const nextKeep = toBool(partial.keepInventoryOnDeath, rules.keepInventoryOnDeath);
    const nextCreative = toBool(partial.creativeMode, rules.creativeMode);
    const nextCoords = toBool(partial.showCoordinates, rules.showCoordinates);
    const changed = (
      nextKeep !== rules.keepInventoryOnDeath
      || nextCreative !== rules.creativeMode
      || nextCoords !== rules.showCoordinates
    );
    if (!changed) return false;

    rules = {
      keepInventoryOnDeath: nextKeep,
      creativeMode: nextCreative,
      showCoordinates: nextCoords
    };
    if (persist) write();
    return true;
  }

  function setKeepInventoryOnDeath(enabled, opts = {}) {
    return setRules({ keepInventoryOnDeath: enabled }, opts);
  }

  function setCreativeMode(enabled, opts = {}) {
    return setRules({ creativeMode: enabled }, opts);
  }

  function setShowCoordinates(enabled, opts = {}) {
    return setRules({ showCoordinates: enabled }, opts);
  }

  function isKeepInventoryOnDeath() {
    return !!rules.keepInventoryOnDeath;
  }

  function isCreativeMode() {
    return !!rules.creativeMode;
  }

  function isShowCoordinates() {
    return !!rules.showCoordinates;
  }

  return {
    read,
    write,
    getRules,
    setRules,
    setKeepInventoryOnDeath,
    setCreativeMode,
    setShowCoordinates,
    isKeepInventoryOnDeath,
    isCreativeMode,
    isShowCoordinates
  };
}
